import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 * Only initializes in production mode
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize if DSN is provided and we're in production
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.log('[Sentry] Skipping initialization - no DSN provided');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || '0.0.0',

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Session replay for error context (only captures on errors)
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring - sample 10% of transactions
    tracesSampleRate: 0.1,

    // Session replay configuration
    // Capture 0% of all sessions, but 100% of sessions with errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Filter out known non-issues
    ignoreErrors: [
      // Browser-specific issues
      'ResizeObserver loop',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network issues that are expected
      'Non-Error promise rejection',
      'Load failed',
      'Failed to fetch',
      // User cancelled requests
      'AbortError',
      'The user aborted a request',
    ],

    // Filter out known non-issue URLs
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
    ],

    // Scrub sensitive data before sending
    beforeSend(event) {
      // Remove authorization headers
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['authorization'];
        delete event.request.headers['Cookie'];
        delete event.request.headers['cookie'];
      }

      // Remove sensitive query params from URLs
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url);
          ['token', 'key', 'apiKey', 'api_key', 'password', 'secret'].forEach((param) => {
            if (url.searchParams.has(param)) {
              url.searchParams.set(param, '[REDACTED]');
            }
          });
          event.request.url = url.toString();
        } catch {
          // URL parsing failed, continue with original
        }
      }

      return event;
    },
  });

  console.log('[Sentry] Initialized successfully');
}

/**
 * Capture an error and send to Sentry
 * In development, also logs to console
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  console.error('[Error]', error);

  if (import.meta.env.DEV) {
    console.error('[Error Context]', context);
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message and send to Sentry
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  if (import.meta.env.DEV) {
    console.log(`[Sentry ${level}]`, message);
  }

  Sentry.captureMessage(message, level);
}

/**
 * Set the current user for Sentry tracking
 */
export function setUser(user: { id: string; email?: string; username?: string }) {
  Sentry.setUser(user);
}

/**
 * Clear the current user (e.g., on logout)
 */
export function clearUser() {
  Sentry.setUser(null);
}

/**
 * Add a breadcrumb for debugging context
 */
export function addBreadcrumb(options: {
  message: string;
  category?: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
}) {
  Sentry.addBreadcrumb({
    message: options.message,
    category: options.category ?? 'app',
    level: options.level ?? 'info',
    data: options.data,
  });
}

/**
 * Set custom tags for filtering in Sentry
 */
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * Set custom context data
 */
export function setContext(name: string, context: Record<string, unknown>) {
  Sentry.setContext(name, context);
}
